
import { useState } from 'react';
import { MockGooglePlace } from '@/utils/mockGoogleMapsApi';
import { useGoogleBusinessSearch } from '@/hooks/useGoogleBusinessSearch';
import { FormSection } from '../types';
import { GeoLocation } from '@/components/business/onboarding/types/locationTypes';
import { useToast } from '@/components/ui/use-toast';

export const useBusinessSearch = (
  updateBusinessDetails: (details: any) => void,
  setSelectedDate: (date: Date | undefined) => void,
  setFormSection: (section: FormSection) => void
) => {
  const { getBusinessDetails } = useGoogleBusinessSearch();
  const [selectedBusiness, setSelectedBusiness] = useState<MockGooglePlace | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  
  // Helper function to determine default timezone based on state
  const getDefaultTimezone = (stateCode: string): string => {
    switch(stateCode) {
      case 'ME': case 'NH': case 'VT': case 'MA': case 'RI': case 'CT': 
      case 'NY': case 'NJ': case 'DE': case 'MD': case 'DC': case 'VA': 
      case 'NC': case 'SC': case 'GA': case 'FL': case 'PA': case 'WV':
        return 'Eastern Standard Time';
      case 'OH': case 'MI': case 'IN': case 'KY': case 'TN': case 'AL': 
      case 'MS': case 'IL': case 'MO': case 'AR': case 'LA': case 'WI': 
      case 'MN': case 'IA':
        return 'Central Standard Time';
      case 'ND': case 'SD': case 'NE': case 'KS': case 'OK': 
      case 'TX': case 'MT': case 'WY': case 'CO': case 'NM':
        return 'Mountain Standard Time';
      case 'AZ':
        return 'Arizona Standard Time';
      case 'ID': case 'UT': case 'NV': case 'CA': case 'OR': case 'WA':
        return 'Pacific Standard Time';
      case 'AK':
        return 'Alaska Standard Time';
      case 'HI':
        return 'Hawaii Standard Time';
      case 'PR': case 'VI':
        return 'Puerto Rico and US Virgin Islands Time';
      case 'GU': case 'MP':
        return 'Chamorro Standard Time';
      case 'AS':
        return 'Samoa Standard Time';
      default:
        return 'Eastern Standard Time'; // Default
    }
  };
  
  const handleBusinessSelect = async (business: MockGooglePlace) => {
    setSelectedBusiness(business);
    setIsCreatingNew(false);
    setIsLoading(true);
    
    try {
      // Get detailed information about the selected business
      const details = await getBusinessDetails(business.place_id);
      
      if (details) {
        // Add coordinates if available
        const coordinates: GeoLocation | undefined = 
          business.geometry?.location ? 
          {
            latitude: business.geometry.location.lat(),
            longitude: business.geometry.location.lng()
          } : 
          undefined;
        
        // Determine default timezone based on state
        const timeZone = details.state ? getDefaultTimezone(details.state) : '';
        
        updateBusinessDetails({
          name: details.name || '',
          shortName: details.name || '',
          phone: details.phone || '',
          email: details.email || '',
          industry: details.industry || '',
          industryVertical: '',
          address: details.address || '',
          city: details.city || '',
          state: details.state || '',
          zip: details.zip || '',
          website: details.website || '',
          founded: details.founded || '',
          coordinates,
          timeZone: timeZone
        });
        
        if (details.founded) {
          const foundedYear = parseInt(details.founded, 10);
          if (!isNaN(foundedYear)) {
            setSelectedDate(new Date(foundedYear, 0, 1));
          }
        }
        
        // Change to form section
        setFormSection('form');
      } else {
        toast({
          title: "Error",
          description: "Could not retrieve business details",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error selecting business:", error);
      toast({
        title: "Error",
        description: "Could not retrieve business details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Modified function signature to match what's needed
  const handleCreateNew = (businessName?: string) => {
    setIsCreatingNew(true);
    setSelectedBusiness(null);
    updateBusinessDetails({
      name: businessName || '',
      shortName: businessName || ''
    });
    // Change to form section
    setFormSection('form');
  };
  
  const resetSearch = () => {
    setSelectedBusiness(null);
    setIsCreatingNew(false);
  };

  return {
    selectedBusiness,
    isCreatingNew,
    isLoading,
    handleBusinessSelect,
    handleCreateNew,
    resetSearch
  };
};
